// Navigation + drill-scope state for the scored Fatigue dashboard.
//
// Mirrors the prototype's global `S` object and go()/setDrillScope()/drill*()
// helpers, as a React context. Screens read the scope through useDash() and
// use scopedWorkers() so every screen filters the same way.

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { GROUPS, REGIONS, WORKERS, workersInGroup, workersInRegion, wArr } from './engine/data';
import type { Worker } from './engine/data';

export type ScreenKey =
  | 'overview'
  | 'past'
  | 'today'
  | 'forward'
  | 'shifts'
  | 'driving'
  | 'resilience'
  | 'compliance'
  | 'recommendations'
  | 'worker';

export type DrillScope = 'company' | 'region' | 'group' | 'individual';

/** Screens that stay put (filtered to the worker) when scope switches to Individual. */
const DATA_SCREENS: ScreenKey[] = ['past', 'forward', 'shifts', 'driving', 'compliance', 'today'];

export interface DashState {
  screen: ScreenKey;
  drillScope: DrillScope;
  activeRegion: string | null;
  activeGroup: number | null;
  activeWorker: string | null;
  pastRange: '7d' | '30d' | '90d';
  todayTab: 'preshift' | 'live';
  forwardTab: 'projection' | 'ot';
  companyTab: 'today' | '7d' | '30d' | '90d';
}

export interface DashApi extends DashState {
  go: (screen: ScreenKey) => void;
  setDrillScope: (scope: DrillScope) => void;
  drillRegion: (rid: string) => void;
  drillGroup: (gid: number) => void;
  drillWorker: (init: string) => void;
  /** Shallow-merge any state (tabs, ranges, ...). */
  patch: (p: Partial<DashState>) => void;
  /** Reset to company scope (breadcrumb "Company"). */
  resetToCompany: () => void;
}

const INITIAL: DashState = {
  screen: 'overview',
  drillScope: 'company',
  activeRegion: null,
  activeGroup: null,
  activeWorker: null,
  pastRange: '30d',
  todayTab: 'preshift',
  forwardTab: 'projection',
  companyTab: 'today',
};

const Ctx = createContext<DashApi | null>(null);

export const DashProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [s, setS] = useState<DashState>(INITIAL);

  const go = useCallback((screen: ScreenKey) => setS((p) => ({ ...p, screen })), []);
  const patch = useCallback((p: Partial<DashState>) => setS((prev) => ({ ...prev, ...p })), []);

  const setDrillScope = useCallback((scope: DrillScope) => {
    setS((p) => {
      const n = { ...p, drillScope: scope };
      if (scope === 'region' && !n.activeRegion) n.activeRegion = Object.keys(REGIONS)[0];
      if (scope === 'group' && n.activeGroup === null) {
        const gids = n.activeRegion ? REGIONS[n.activeRegion].groups : GROUPS.map((g) => g.id);
        n.activeGroup = gids[0];
      }
      if (scope === 'individual') {
        if (!n.activeWorker) {
          const pool =
            n.activeGroup !== null
              ? workersInGroup(n.activeGroup)
              : n.activeRegion
                ? workersInRegion(n.activeRegion)
                : wArr();
          n.activeWorker = pool.length ? pool[0].init : Object.keys(WORKERS)[0];
        }
        if (!DATA_SCREENS.includes(n.screen)) n.screen = 'worker';
        return n;
      }
      if (n.screen === 'worker') n.screen = 'overview';
      return n;
    });
  }, []);

  const drillRegion = useCallback(
    (rid: string) => setS((p) => ({ ...p, activeRegion: rid, drillScope: 'region' })),
    [],
  );
  const drillGroup = useCallback((gid: number) => setS((p) => ({ ...p, activeGroup: gid, drillScope: 'group' })), []);
  const drillWorker = useCallback((init: string) => setS((p) => ({ ...p, activeWorker: init, screen: 'worker' })), []);
  const resetToCompany = useCallback(
    () => setS((p) => ({ ...p, drillScope: 'company', activeRegion: null, activeGroup: null, screen: 'overview' })),
    [],
  );

  const value = useMemo<DashApi>(
    () => ({ ...s, go, setDrillScope, drillRegion, drillGroup, drillWorker, patch, resetToCompany }),
    [s, go, setDrillScope, drillRegion, drillGroup, drillWorker, patch, resetToCompany],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function useDash(): DashApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useDash must be used inside <DashProvider>');
  return v;
}

/** The workers in the current drill scope (company → all, region, group, or the single individual). */
export function scopedWorkers(s: DashState): Worker[] {
  if (s.drillScope === 'individual' && s.activeWorker) return [WORKERS[s.activeWorker]];
  if (s.drillScope === 'group' && s.activeGroup !== null) return workersInGroup(s.activeGroup);
  if (s.drillScope === 'region' && s.activeRegion) return workersInRegion(s.activeRegion);
  return wArr();
}

/** Human label for the current scope, for card subtitles. */
export function scopeLabel(s: DashState): string {
  if (s.drillScope === 'individual' && s.activeWorker) return WORKERS[s.activeWorker].name;
  if (s.drillScope === 'group' && s.activeGroup !== null) return GROUPS[s.activeGroup].name;
  if (s.drillScope === 'region' && s.activeRegion) return REGIONS[s.activeRegion].name;
  return 'Company';
}
